import { getToken } from '@/core/token';
import { execute } from '@/graphql/execute';
import { Srid } from '@/graphql/graphql';
import { sridsQuery } from '@/queries/feature.query';
import { atom } from 'jotai';
import { userAtom } from './user.atom';

const SRIDS_STORAGE_KEY = '@gis.srids.3';
const CACHE_DURATION_DAYS = 7;

interface SridsCache {
  srids: Srid[];
  downloadDate: string;
}

const loadSridsFromCache = (): Srid[] | null => {
  try {
    const cached = localStorage.getItem(SRIDS_STORAGE_KEY);
    if (!cached) return null;

    const parsed: SridsCache = JSON.parse(cached);
    const downloadDate = new Date(parsed.downloadDate);
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() - CACHE_DURATION_DAYS);

    if (downloadDate > expirationDate) {
      return parsed.srids;
    }
  } catch (error) {
    console.warn('Failed to load srids from cache:', error);
  }
  return null;
};

const saveSridsToCache = (srids: Srid[]) => {
  try {
    const cache: SridsCache = {
      srids,
      downloadDate: new Date().toISOString(),
    };
    localStorage.setItem(SRIDS_STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('Failed to save srids to cache:', error);
  }
};

// Base atom for srids data
const sridsBaseAtom = atom<Srid[]>([]);

// Write-only atom that loads srids
export const sridsLoaderAtom = atom(null, async (get, set) => {
  // Try to load from cache first
  const cachedSrids = loadSridsFromCache();
  if (cachedSrids) {
    set(sridsBaseAtom, cachedSrids);
    return cachedSrids;
  }

  // Check if user is authenticated
  const user = get(userAtom);
  const token = getToken();

  if (!user || !token) {
    set(sridsBaseAtom, []);
    return [];
  }

  try {
    // Fetch fresh data from API
    const data = await execute(sridsQuery);
    const srids = data.srids ?? [];

    srids.unshift({
      srid: -1,
      name: 'Local',
      projection: 'No projection',
    });

    // Update atom and cache
    set(sridsBaseAtom, srids);
    saveSridsToCache(srids);

    return srids;
  } catch (error) {
    console.error('Failed to fetch srids:', error);
    set(sridsBaseAtom, []);
    return [];
  }
});

// Read-only atom for srids data
export const sridsAtom = atom((get) => get(sridsBaseAtom));

// Atom that automatically reloads when user changes
export const sridsWithUserReloadAtom = atom(
  (get) => {
    // This will cause the atom to re-evaluate when userAtom changes
    const user = get(userAtom);
    const srids = get(sridsAtom);
    return { srids, user };
  },
  async (_, set) => {
    await set(sridsLoaderAtom);
  },
);

// Helper function to manually reload srids
export const reloadSrids = async (set: (atom: any, value: any) => void) => {
  await set(sridsLoaderAtom, undefined);
};
